import {Component, computed, effect, inject, input, InputSignal, model, ModelSignal, Signal, signal, viewChild, ViewEncapsulation, WritableSignal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {DxNumberBoxModule, DxPopupModule} from 'devextreme-angular';
import {SharedModule} from '../../../../../shared/shared.module';
import {RepasDTO} from '../../../../../core/dtos/repas-dto';
import {JourSemaine} from '../../../../../core/constants';
import {Cmcr__CmcCa__PlcDto} from '../../../../../core/dtos/cmcr__cmcca__plc-dto';
import {TreeListComponent} from '../../../../../shared/ui/tree-list/tree-list.component';
import {ButtonThemeEnum} from '../../../../../shared/ui/button/button-theme-enum';
import {UtilsService} from '../../../../../core/utils/utils.service';
import {find as _find} from 'lodash';

type UpdateMode = 'effectif' | 'cout';

interface RepasTreeNode {
  libelle: string;
  children: RegimeTreeNode[];
}

interface RegimeTreeNode {
  id: number;
  libelle: string;
  parentId: number;
}

@Component({
  selector: 'yo-dialog-update-effectifs',
  templateUrl: './dialog-update-effectifs.component.html',
  styleUrls: ['./dialog-update-effectifs.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, DxPopupModule, DxNumberBoxModule, SharedModule],
  encapsulation: ViewEncapsulation.None
})
export class DialogUpdateEffectifsComponent {

  visible: ModelSignal<boolean> = model<boolean>(false);
  mode: InputSignal<UpdateMode> = input<UpdateMode>('effectif');
  repasList: InputSignal<RepasDTO[]> = input<RepasDTO[]>([]);
  joursSemaine: InputSignal<JourSemaine[]> = input<JourSemaine[]>([]);
  cmcrCmcCaPlcList: InputSignal<Cmcr__CmcCa__PlcDto[]> = input<Cmcr__CmcCa__PlcDto[]>([]);

  treeListJours: Signal<TreeListComponent | undefined> = viewChild<TreeListComponent>('treeListJours');
  treeListRepas: Signal<TreeListComponent | undefined> = viewChild<TreeListComponent>('treeListRepas');

  readonly ButtonThemeEnum = ButtonThemeEnum;
  #utils = inject(UtilsService);

  repas: WritableSignal<RepasTreeNode[]> = signal<RepasTreeNode[]>([]);
  inputValue: WritableSignal<number | null> = signal<number | null>(null);
  selectedJoursSemaine: JourSemaine[] = [];
  selectedRepasList: RepasDTO[] = [];
  selectedRegimeList: RegimeTreeNode[] = [];

  dialogTitle: Signal<string> = computed(() => this.mode() === 'effectif' ? 'Modifier les effectifs prévisionnels' : 'Modifier les coûts prévisionnels');
  fieldLabel: Signal<string> = computed(() => this.mode() === 'effectif' ? 'Effectif prévisionnel' : 'Coût prévisionnel');
  fieldUnit: Signal<string> = computed(() => this.mode() === 'effectif' ? 'pers.' : '€');

  constructor() {
    effect(() => {
      const list = this.repasList();
      if (list?.length) {
        this.repas.set(this.#buildRepasTree(list));
      }
    });
  }

  #buildRepasTree(list: RepasDTO[]): RepasTreeNode[] {
    return list.map(item => ({
      libelle: item.libelle,
      children: (item.regimesAlimentaire || []).map(regime => ({
        id: regime.id,
        libelle: regime.libelle,
        parentId: item.id
      }))
    }));
  }

  closeDialog(): void {
    this.selectedJoursSemaine = [];
    this.selectedRepasList = [];
    this.selectedRegimeList = [];
    this.inputValue.set(null);
    this.treeListJours()?.clearSelection();
    this.treeListRepas()?.clearSelection();
    this.visible.set(false);
  }

  onChangeJoursSemaine = (joursSemaine: JourSemaine[]) => this.selectedJoursSemaine = joursSemaine;

  onChangeRepasRegime = (regimeList: RegimeTreeNode[]) => {
    this.selectedRegimeList = [];
    this.selectedRepasList = [];
    regimeList.forEach(row => {
      if (!this.selectedRepasList.find(item => item.id === row.parentId)) {
        this.selectedRepasList.push(this.repasList().find(item => item.id === row.parentId));
      }
      if (!this.selectedRegimeList.find(item => item.id === row.id)) {
        this.selectedRegimeList.push(row);
      }
    });
  };

  save(): void {
    const list = this.cmcrCmcCaPlcList();
    if (this.#utils.isCollectionNullOrEmpty(list)) return;
    if (this.inputValue() == null) return;

    const field = this.mode() === 'effectif' ? 'effectifPrevisionnel' : 'coutPrevisionnel';

    for (const jourSemaine of this.selectedJoursSemaine) {
      for (const repas of this.selectedRepasList) {
        for (const regime of this.selectedRegimeList) {
          const item = _find(list, {
            idRepas: repas.id,
            idJourSemaine: jourSemaine.value,
            idRegime: regime.id
          }) as Cmcr__CmcCa__PlcDto;

          if (!this.#utils.isNullOrEmpty(item)) {
            item[field] = this.inputValue();
          }
        }
      }
    }
    this.closeDialog();
  }
}
